import {dataTestId} from '@hconnect/uikit'
import moment, {Moment} from 'moment'
import {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {usePlantConfig} from '../../hooks/usePlantConfigData'
import {LatestPeakLoadWindow} from '../../interfaces/api'
import {longDayNameAndTimeRangeFormatter} from '../../selectors/time'

import {AlertWithLeftBorder} from './AlertWithLeftBorder'

interface PeakLoadWindowAlertProps {
  peakLoadWindows?: LatestPeakLoadWindow[]
  startOfPlan: Moment
  endOfPlan: Moment
}

const usePeakLoadWindowAlertMessage = (closestPeakLoadWindow?: LatestPeakLoadWindow) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()

  const {timezone_id: timezoneId} = usePlantConfig()

  return useMemo(() => {
    if (!closestPeakLoadWindow) return ''

    const formattedTimeFrame = longDayNameAndTimeRangeFormatter(
      [moment.utc(closestPeakLoadWindow.start), moment.utc(closestPeakLoadWindow.end)],
      timezoneId,
      language
    )

    return t('alerts.peakLoadWindow', {
      timeframe: formattedTimeFrame
    })
  }, [closestPeakLoadWindow, language, timezoneId, t])
}

export const PeakLoadWindowAlert: React.FC<PeakLoadWindowAlertProps> = ({
  peakLoadWindows,
  startOfPlan,
  endOfPlan
}) => {
  const currentDate = moment.utc()
  const startOfPlanUTC = startOfPlan.clone().utc()
  const endOfPlanUTC = endOfPlan.clone().utc()

  const closestPeakLoadWindow = peakLoadWindows?.find((window) => {
    return moment.utc(window.start).isAfter(currentDate)
  })

  const shouldShowAlert = !!(
    currentDate.isBetween(startOfPlanUTC, endOfPlanUTC, undefined, '[]') && closestPeakLoadWindow
  )

  const [showPeakLoadWindowAlert, setShowPeakLoadWindowAlert] = useState(shouldShowAlert)

  const peakLoadWindowAlertMessage = usePeakLoadWindowAlertMessage(closestPeakLoadWindow)

  useEffect(() => {
    setShowPeakLoadWindowAlert(shouldShowAlert)
  }, [shouldShowAlert])

  if (showPeakLoadWindowAlert) {
    return (
      <AlertWithLeftBorder
        severity="error"
        onClose={() => setShowPeakLoadWindowAlert(false)}
        {...dataTestId('peak-load-window-alert')}
      >
        {peakLoadWindowAlertMessage}
      </AlertWithLeftBorder>
    )
  }

  return null
}
